import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { useAiConfirm } from "../components/AiConfirmDialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FloppyDisk, Plus, Trash, ArrowLeft, Users, Sparkle, FileArrowDown, ShieldCheck } from "@phosphor-icons/react";
import InvestigationReview from "../components/InvestigationReview";

const STATUS = ["open", "in_review", "closed"];
const emptyWhys = () => Array.from({ length: 5 }, (_, i) => ({ level: i + 1, why: "", solution: "" }));

// Hoisted stateless helper (react/no-unstable-nested-components).
const AiBtn = ({ kind, label, aiEnabled, aiBusy, onAssist, blockTitle }) => (
  <Button data-testid={`ai-${kind}-btn`} variant="outline" size="sm" disabled={!aiEnabled || aiBusy === kind}
    title={!aiEnabled ? (blockTitle || "AI features are turned off for this entity") : undefined}
    onClick={() => onAssist(kind)} className="rounded-none border-primary/40 text-primary">
    <Sparkle size={14} weight="fill" className="mr-1" />{aiBusy === kind ? "Thinking…" : label}
  </Button>
);

export default function InvestigationDetail() {
  const { confirmAi, aiConfirmDialog } = useAiConfirm();
  const { id } = useParams();
  const nav = useNavigate();
  const { user, aiEnabled, aiConfidentialBlocked } = useAuth();
  const [inv, setInv] = useState(null);
  const [codes, setCodes] = useState([]);
  const [pool, setPool] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const [aiQuestions, setAiQuestions] = useState([]);

  const load = useCallback(() => {
    api.get(`/investigations/${id}`).then((r) => {
      const d = r.data;
      if (!d.whys || d.whys.length === 0) d.whys = emptyWhys();
      if (!d.causes) d.causes = { root: null, contributing: [] };
      setInv(d);
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/investigations"); });
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/investigations/meta/cause-codes").then((r) => setCodes(r.data)).catch(() => {});
  }, []);

  const isLead = inv && user?.id === inv.lead_id;
  const set = (patch) => setInv((v) => ({ ...v, ...patch }));

  const [cleanupMode, setCleanupMode] = useState(false);
  useEffect(() => {
    if (user?.is_superadmin) api.get("/console/platform-settings").then((r) => setCleanupMode(!!r.data?.cleanup_mode)).catch(() => {});
  }, [user]);
  const forceClose = async () => {
    if (!window.confirm("Force-close this investigation? This bypasses the lead-only rule and the closure checklist. (Cleanup Mode)")) return;
    try { await api.post(`/console/investigations/${id}/force-close`); toast.success("Investigation force-closed."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const forceDelete = async () => {
    if (!window.confirm("PERMANENTLY delete this investigation? This cannot be undone. (Cleanup Mode)")) return;
    try { await api.delete(`/console/investigations/${id}`); toast.success("Investigation deleted."); nav("/investigations"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: inv.title, problem: inv.problem, incident_summary: inv.incident_summary,
        executive_summary: inv.executive_summary, vrf: inv.vrf, vsl: inv.vsl,
        violation_indicated: !!inv.violation_indicated, existing_controls: inv.existing_controls,
        standard_id: inv.standard_id, requirement_ids: inv.requirement_ids,
        requirement_summary: inv.requirement_summary,
        events: inv.events, whys: inv.whys, causes: inv.causes,
        cause_narrative: inv.cause_narrative,
        solutions: inv.solutions, action_items: inv.action_items, interviews: inv.interviews,
        report_date: inv.report_date,
      };
      const { data } = await api.patch(`/investigations/${id}`, payload);
      setInv((v) => ({ ...data, whys: data.whys?.length ? data.whys : emptyWhys(), causes: data.causes || { root: null, contributing: [] } }));
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (status) => {
    try { const { data } = await api.patch(`/investigations/${id}`, { status }); set({ status: data.status }); toast.success(`Status: ${status}`); }
    catch (e) {
      const detail = e.response?.data?.detail;
      if (status === "closed" && detail && typeof detail === "object") {
        toast.error(detail.message || "Complete the closure checklist before closing.");
      } else { toast.error(formatApiError(detail)); }
    }
  };

  const download = async (path, fallbackName) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename=([^;]+)/);
      const name = m ? m[1].trim() : fallbackName;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || "Download failed")); }
  };

  const openTeam = async () => {
    try { const { data } = await api.get("/investigations/meta/investigators"); setPool(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleMember = async (uid) => {
    const cur = inv.team_member_ids || [];
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    try { const { data } = await api.put(`/investigations/${id}/team`, { member_ids: next }); set({ team_member_ids: data.team_member_ids, team: data.team }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // generic array section helpers
  const _rk = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r-${Date.now()}-${Math.random()}`;
  const addRow = (key, row) => set({ [key]: [...(inv[key] || []), { ...row, _rk: _rk() }] });
  const editRow = (key, idx, patch) => set({ [key]: inv[key].map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  const delRow = (key, idx) => set({ [key]: inv[key].filter((_, i) => i !== idx) });

  const aiAssist = async (kind) => {
    if (!(await confirmAi())) return;
    setAiBusy(kind);
    try {
      const res = await runAiJob(`/investigations/${id}/ai/${kind}?confirm_confidential=1`);
      if (kind === "whys" && Array.isArray(res.whys)) {
        const w = emptyWhys().map((row, i) => res.whys[i] ? { level: i + 1, why: res.whys[i].why || "", solution: res.whys[i].solution || "" } : row);
        set({ whys: w });
      } else if (kind === "solutions" && Array.isArray(res.solutions)) {
        set({ solutions: [...(inv.solutions || []), ...res.solutions.map((s) => ({ description: s.description || "", owner: s.owner || "", due_date: s.due_date || "", status: s.status || "Not Started" }))] });
      } else if (kind === "causes") {
        set({ causes: { root: res.root || inv.causes.root, contributing: Array.isArray(res.contributing) ? res.contributing : (inv.causes.contributing || []) }, cause_narrative: res.cause_narrative || inv.cause_narrative });
      } else if (kind === "exec-summary") {
        set({ executive_summary: res.executive_summary || inv.executive_summary });
      } else if (kind === "cause-narrative") {
        set({ cause_narrative: res.cause_narrative || inv.cause_narrative });
      } else if (kind === "interview-questions") {
        setAiQuestions(Array.isArray(res.questions) ? res.questions : []);
      }
      toast.success("AI suggestions added — review, edit, then Save");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiBusy(""); }
  };

  // Bind common props to the module-level AiBtn to keep call sites tidy.
  const aiBtnProps = { aiEnabled: aiEnabled && !aiConfidentialBlocked, aiBusy, onAssist: aiAssist, blockTitle: aiConfidentialBlocked ? "Investigation AI is blocked while AI is set to Public-information only" : undefined };

  if (!inv) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const codeLabel = (c) => { const f = codes.find((x) => x.code === c); return f ? `${f.code} — ${f.title}` : c; };

  return (
    <div className="p-6 space-y-4" data-testid="investigation-detail">
      {aiConfirmDialog}
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => nav("/investigations")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft size={16} /> Investigations</button>
        <div className="flex items-center gap-2">
          <Button data-testid="inv-report-btn" variant="outline" onClick={() => download(`/investigations/${id}/report.docx`, "investigation-report.docx")} className="rounded-none">
            <FileArrowDown size={16} className="mr-1" />Report
          </Button>
          <Button data-testid="inv-align-btn" variant="outline" disabled={!inv.violation_indicated}
            title={!inv.violation_indicated ? "Mark 'violation indicated' on Overview to enable the Align self-report" : undefined}
            onClick={() => download(`/investigations/${id}/align.docx`, "align-self-report.docx")} className="rounded-none">
            <FileArrowDown size={16} className="mr-1" />Align form
          </Button>
          {isLead && (
            <Select value={inv.status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="inv-status-select" className="rounded-none h-9 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button data-testid="inv-save-btn" onClick={save} disabled={saving} className="rounded-none"><FloppyDisk size={16} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
          {user?.is_superadmin && cleanupMode && (
            <>
              <Button data-testid="inv-force-close-btn" variant="outline" onClick={forceClose}
                className="rounded-none text-red-700 border-red-400 bg-red-50 hover:bg-red-100">Force Close</Button>
              <Button data-testid="inv-force-delete-btn" variant="outline" onClick={forceDelete}
                className="rounded-none text-red-700 border-red-400 bg-red-50 hover:bg-red-100"><Trash size={16} /></Button>
            </>
          )}
        </div>
      </div>

      <div>
        <Input data-testid="inv-title-edit" value={inv.title} onChange={(e) => set({ title: e.target.value })} className="rounded-none font-display font-bold text-xl border-0 border-b px-0" />
        <div className="text-xs text-muted-foreground mt-1">Lead: {inv.lead_name} · Team: {(inv.team || []).map((m) => m.name).join(", ")}</div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-none flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Sequence of Events</TabsTrigger>
          <TabsTrigger value="whys" data-testid="tab-whys">5-Why</TabsTrigger>
          <TabsTrigger value="causes" data-testid="tab-causes">Cause Mapping</TabsTrigger>
          <TabsTrigger value="solutions" data-testid="tab-solutions">Solutions</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions">Action Items</TabsTrigger>
          <TabsTrigger value="interviews" data-testid="tab-interviews">Interviews</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review"><ShieldCheck size={14} className="mr-1" />Review &amp; Closure</TabsTrigger>
          {isLead && <TabsTrigger value="team" data-testid="tab-team" onClick={openTeam}>Team</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Standard</Label><Input value={inv.standard_id || ""} onChange={(e) => set({ standard_id: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Requirement(s)</Label><Input value={(inv.requirement_ids || []).join(", ")} onChange={(e) => set({ requirement_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="rounded-none mt-1" /></div>
            <div><Label>Violation Risk Factor (VRF)</Label><Input value={inv.vrf || ""} onChange={(e) => set({ vrf: e.target.value })} className="rounded-none mt-1" placeholder="Lower / Medium / High" /></div>
            <div><Label>Violation Severity Level (VSL)</Label><Input value={inv.vsl || ""} onChange={(e) => set({ vsl: e.target.value })} className="rounded-none mt-1" placeholder="Lower / Moderate / High / Severe" /></div>
          </div>
          <div><Label>Incident summary</Label><Textarea value={inv.incident_summary || ""} onChange={(e) => set({ incident_summary: e.target.value })} className="rounded-none mt-1 min-h-[80px]" /></div>
          <div><Label>Problem statement</Label><Textarea value={inv.problem || ""} onChange={(e) => set({ problem: e.target.value })} className="rounded-none mt-1 min-h-[80px]" /></div>
          <div><Label>Executive summary</Label><Textarea value={inv.executive_summary || ""} onChange={(e) => set({ executive_summary: e.target.value })} className="rounded-none mt-1 min-h-[100px]" placeholder="What happened and the primary requirement violation." /></div>
          <div className="-mt-2"><AiBtn kind="exec-summary" label="AI: draft executive summary" {...aiBtnProps} /></div>
          <div><Label>Existing controls already in place</Label><Textarea value={inv.existing_controls || ""} onChange={(e) => set({ existing_controls: e.target.value })} className="rounded-none mt-1 min-h-[80px]" /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="inv-violation-check" checked={!!inv.violation_indicated} onCheckedChange={(c) => set({ violation_indicated: !!c })} /> A violation is indicated (enables Align self-report packaging)</label>
        </TabsContent>

        <TabsContent value="events" className="pt-4 space-y-3">
          <p className="text-xs text-muted-foreground">Chronological events with human-performance checks. The goal is root cause, not blame.</p>
          {(inv.events || []).map((ev, i) => (
            <div key={ev._rk || `event-${i}`} data-testid={`event-row-${i}`} className="border border-border p-3 space-y-2">
              <div className="grid grid-cols-12 gap-2">
                <Input value={ev.event_no || i + 1} onChange={(e) => editRow("events", i, { event_no: e.target.value })} className="rounded-none col-span-1" />
                <Input type="datetime-local" value={ev.when || ""} onChange={(e) => editRow("events", i, { when: e.target.value })} className="rounded-none col-span-4" />
                <Input value={ev.description || ""} onChange={(e) => editRow("events", i, { description: e.target.value })} placeholder="What happened" className="rounded-none col-span-6" />
                <Button variant="ghost" size="icon" onClick={() => delRow("events", i)} className="col-span-1"><Trash size={16} /></Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input value={ev.human_perf || ""} onChange={(e) => editRow("events", i, { human_perf: e.target.value })} placeholder="Human performance concern / training / procedure available?" className="rounded-none" />
                <Input value={ev.controls || ""} onChange={(e) => editRow("events", i, { controls: e.target.value })} placeholder="Controls in place / sufficient?" className="rounded-none" />
              </div>
            </div>
          ))}
          <Button data-testid="add-event-btn" variant="outline" onClick={() => addRow("events", { event_no: (inv.events?.length || 0) + 1, when: "", description: "", human_perf: "", controls: "" })} className="rounded-none"><Plus size={16} className="mr-1" />Add event</Button>
        </TabsContent>

        <TabsContent value="whys" className="pt-4 space-y-3">
          <p className="text-xs text-muted-foreground">Iterative "why" analysis to reach the underlying cause.</p>
          <div className="-mt-1 mb-1"><AiBtn kind="whys" label="AI: suggest 5-Why" {...aiBtnProps} /></div>

          {inv.whys.map((w, i) => (
            <div key={`why-${w.level}`} className="grid grid-cols-12 gap-2 items-start" data-testid={`why-row-${i}`}>
              <div className="col-span-1 pt-2 text-sm font-semibold">Why {w.level}</div>
              <Textarea value={w.why || ""} onChange={(e) => editRow("whys", i, { why: e.target.value })} placeholder="Why did this happen?" className="rounded-none col-span-6 min-h-[44px]" />
              <Textarea value={w.solution || ""} onChange={(e) => editRow("whys", i, { solution: e.target.value })} placeholder="Possible solution" className="rounded-none col-span-5 min-h-[44px]" />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="causes" className="pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">NERC Enforcement Cause Codes — one root cause, plus any contributing causes.</p>
            <AiBtn kind="causes" label="AI: suggest cause codes" {...aiBtnProps} />
          </div>
          <div>
            <Label>Root cause code</Label>
            <Select value={inv.causes.root || ""} onValueChange={(v) => set({ causes: { ...inv.causes, root: v } })}>
              <SelectTrigger data-testid="root-cause-select" className="rounded-none mt-1"><SelectValue placeholder="Select root cause" /></SelectTrigger>
              <SelectContent className="max-h-72">{codes.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contributing cause codes</Label>
            <div className="mt-2 grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto border border-border p-3">
              {codes.map((c) => {
                const checked = (inv.causes.contributing || []).includes(c.code);
                return (
                  <label key={c.code} className="flex items-start gap-2 text-xs">
                    <Checkbox checked={checked} onCheckedChange={(ck) => {
                      const cur = inv.causes.contributing || [];
                      set({ causes: { ...inv.causes, contributing: ck ? [...cur, c.code] : cur.filter((x) => x !== c.code) } });
                    }} />
                    <span><b>{c.code}</b> — {c.title}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div><Label>Root & contributing cause narrative</Label><Textarea value={inv.cause_narrative || ""} onChange={(e) => set({ cause_narrative: e.target.value })} className="rounded-none mt-1 min-h-[100px]" /></div>
          <div className="-mt-2"><AiBtn kind="cause-narrative" label="AI: draft cause narrative" {...aiBtnProps} /></div>
        </TabsContent>

        <TabsContent value="solutions" className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Suggested mitigations / corrective actions to prevent recurrence.</p>
            <AiBtn kind="solutions" label="AI: suggest mitigations" {...aiBtnProps} />
          </div>
          {(inv.solutions || []).map((s, i) => (
            <div key={s._rk || `solution-${i}`} data-testid={`solution-row-${i}`} className="grid grid-cols-12 gap-2 items-center border border-border p-2">
              <Input value={s.description || ""} onChange={(e) => editRow("solutions", i, { description: e.target.value })} placeholder="Mitigation / corrective action" className="rounded-none col-span-5" />
              <Input value={s.owner || ""} onChange={(e) => editRow("solutions", i, { owner: e.target.value })} placeholder="Owner" className="rounded-none col-span-2" />
              <Input type="date" value={s.due_date || ""} onChange={(e) => editRow("solutions", i, { due_date: e.target.value })} className="rounded-none col-span-2" />
              <Input value={s.status || ""} onChange={(e) => editRow("solutions", i, { status: e.target.value })} placeholder="Status" className="rounded-none col-span-2" />
              <Button variant="ghost" size="icon" onClick={() => delRow("solutions", i)} className="col-span-1"><Trash size={16} /></Button>
            </div>
          ))}
          <Button data-testid="add-solution-btn" variant="outline" onClick={() => addRow("solutions", { description: "", owner: "", due_date: "", status: "Not Started" })} className="rounded-none"><Plus size={16} className="mr-1" />Add mitigation</Button>
        </TabsContent>

        <TabsContent value="actions" className="pt-4 space-y-3">
          <p className="text-xs text-muted-foreground">Investigation action items — data/evidence to collect or tasks to perform.</p>
          {(inv.action_items || []).map((a, i) => (
            <div key={a._rk || `action-${i}`} data-testid={`action-row-${i}`} className="grid grid-cols-12 gap-2 items-center border border-border p-2">
              <Input value={a.action || ""} onChange={(e) => editRow("action_items", i, { action: e.target.value })} placeholder="Action item" className="rounded-none col-span-5" />
              <Input value={a.owner || ""} onChange={(e) => editRow("action_items", i, { owner: e.target.value })} placeholder="Owner" className="rounded-none col-span-2" />
              <Input type="date" value={a.due_date || ""} onChange={(e) => editRow("action_items", i, { due_date: e.target.value })} className="rounded-none col-span-2" />
              <Input value={a.status || ""} onChange={(e) => editRow("action_items", i, { status: e.target.value })} placeholder="Status" className="rounded-none col-span-2" />
              <Button variant="ghost" size="icon" onClick={() => delRow("action_items", i)} className="col-span-1"><Trash size={16} /></Button>
            </div>
          ))}
          <Button data-testid="add-action-btn" variant="outline" onClick={() => addRow("action_items", { action: "", owner: "", due_date: "", status: "Open" })} className="rounded-none"><Plus size={16} className="mr-1" />Add action item</Button>
        </TabsContent>

        <TabsContent value="interviews" className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Interviews — capture who was interviewed and key findings. The aim is root cause, not blame.</p>
            <AiBtn kind="interview-questions" label="AI: suggest questions" {...aiBtnProps} />
          </div>
          {aiQuestions.length > 0 && (
            <div data-testid="ai-questions-panel" className="border border-primary/30 bg-primary/5 p-3 space-y-1">
              <div className="text-xs font-semibold text-primary">Suggested questions (objective, non-accusatory):</div>
              <ul className="list-disc pl-5 text-sm space-y-0.5">{aiQuestions.map((q, i) => <li key={`aiq-${i}-${(q || "").slice(0, 40)}`}>{q}</li>)}</ul>
            </div>
          )}
          {(inv.interviews || []).map((iv, i) => (
            <div key={iv._rk || `interview-${i}`} data-testid={`interview-row-${i}`} className="border border-border p-3 space-y-2">
              <div className="grid grid-cols-12 gap-2">
                <Input value={iv.person || ""} onChange={(e) => editRow("interviews", i, { person: e.target.value })} placeholder="Person interviewed" className="rounded-none col-span-4" />
                <Input value={iv.role || ""} onChange={(e) => editRow("interviews", i, { role: e.target.value })} placeholder="Role / title" className="rounded-none col-span-4" />
                <Input type="date" value={iv.date || ""} onChange={(e) => editRow("interviews", i, { date: e.target.value })} className="rounded-none col-span-3" />
                <Button variant="ghost" size="icon" onClick={() => delRow("interviews", i)} className="col-span-1"><Trash size={16} /></Button>
              </div>
              <Textarea value={iv.notes || ""} onChange={(e) => editRow("interviews", i, { notes: e.target.value })} placeholder="Questions asked & key findings" className="rounded-none min-h-[70px]" />
            </div>
          ))}
          <Button data-testid="add-interview-btn" variant="outline" onClick={() => addRow("interviews", { person: "", role: "", date: "", notes: "" })} className="rounded-none"><Plus size={16} className="mr-1" />Add interview</Button>
        </TabsContent>

        <TabsContent value="review" className="pt-0">
          <InvestigationReview id={id} inv={inv} isLead={isLead} />
        </TabsContent>

        {isLead && (
          <TabsContent value="team" className="pt-4 space-y-3" data-testid="team-panel">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Users size={14} /> Appoint investigators to this case. Only assigned members can view it.</p>
            {pool.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm border border-border p-2" data-testid={`team-opt-${p.id}`}>
                <Checkbox checked={(inv.team_member_ids || []).includes(p.id)} disabled={p.id === inv.lead_id} onCheckedChange={() => toggleMember(p.id)} />
                <span>{p.name} <span className="text-muted-foreground">({p.email}) · {p.investigation_role?.replace(/_/g, " ")}</span>{p.id === inv.lead_id ? " — Lead" : ""}</span>
              </label>
            ))}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
